import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Slider from 'react-slick'
import bkBg from '../assets/img/bk_3.jpg'
import appIcon from '../assets/img/logo-aleph-rosu.png'
import alephLogo from '../assets/img/Aleph Logo.svg'
import lastFrameImg from '../assets/img/last_frame.png'
import V3VideoSplash from './V3VideoSplash'
import { trackHomepageView } from '../services/usageTracking'
import './V3Home.css'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

const API_BASE = 'https://backend.alephmedia.ro/api'

  // Mapare tipuri widget -> endpoint API
  const WIDGET_TYPE_MAP = {
    'live-streams': { endpoint: 'live-streams', nameField: 'name', linkBase: '/v3.0/live' },
    'sites': { endpoint: 'sites', nameField: 'name', linkBase: '/v3.0/sites' },
    'programs': { endpoint: 'programs', nameField: 'title', linkBase: '/v3.0/programs' },
    'people': { endpoint: 'people', nameField: 'name', linkBase: '/v3.0/people' },
    'radios': { endpoint: 'radios', nameField: 'title', linkBase: '/v3.0/radios' },
    'watching': { endpoint: 'watching', nameField: 'title', linkBase: '/v3.0/programs' },
    'whats-new': { endpoint: 'programs', nameField: 'title', linkBase: '/v3.0/programs' },
    'news': { endpoint: 'news', nameField: 'title', linkBase: null } // Link-ul vine direct din articol
  }

function decodeHtmlEntities(text) {
  if (!text) return ''
  const textarea = document.createElement('textarea')
  textarea.innerHTML = text
  return textarea.value
}

function extractName(item = {}, field) {
  if (!item) return '—'
  
  let rawText = ''
  // Verificăm mai întâi câmpurile cu prefix v3_
  if (field === 'name' && item.v3_name) {
    rawText = item.v3_name
  } else if (field === 'title' && item.v3_title) {
    rawText = item.v3_title
  } else if (field === 'name' && item.name) {
    rawText = item.name
  } else if (field === 'title' && item.title) {
    rawText = item.title
  } else if (item.first_name || item.last_name) {
    rawText = `${item.first_name ?? ''} ${item.last_name ?? ''}`.trim()
  } else {
    rawText = item.v3_name || item.v3_title || item.name || item.title || '—'
  }
  
  return decodeHtmlEntities(rawText)
}

// Extrage mediaId din URL-ul JWPlayer manifest sau video
function extractJWPlayerMediaId(url) {
  if (!url || typeof url !== 'string') return null
  
  // Format 1: https://cdn.jwplayer.com/manifests/BbisIoAA.m3u8
  // Format 2: https://content.jwplatform.com/manifests/BbisIoAA.m3u8
  let match = url.match(/manifests\/([^\/]+)\.m3u8/i)
  if (match) return match[1]
  
  // Format 3: https://cdn.jwplayer.com/videos/Mxk0VcUm-tJXulhhN.mp4
  // MediaID este partea dinainte de primul "-"
  match = url.match(/jwplayer\.com\/videos\/([^-]+)/i)
  if (match) return match[1]
  
  // Format 4: https://content.jwplatform.com/videos/Mxk0VcUm-tJXulhhN.mp4
  match = url.match(/jwplatform\.com\/videos\/([^-]+)/i)
  if (match) return match[1]
  
  return null
}

// Obține poster-ul JWPlayer pentru un mediaId
function getJWPlayerPosterUrl(mediaId) {
  if (!mediaId) return null
  return `https://cdn.jwplayer.com/v2/media/${mediaId}/poster.jpg`
}

// Obține URL-ul pentru preview MP4 de la JWPlayer (dacă există)
function getJWPlayerPreviewUrl(mediaId) {
  if (!mediaId) return null
  // JWPlayer generează un preview MP4 la acest URL (poster.mp4, nu preview.mp4)
  return `https://cdn.jwplayer.com/v2/media/${mediaId}/poster.mp4`
}

// Verifică dacă un URL este un manifest video sau nu este o imagine validă
function isValidImageUrl(url) {
  if (!url || typeof url !== 'string') return false
  // Excludem manifestele video și alte tipuri de fișiere non-image
  const videoExtensions = ['.m3u8', '.mp4', '.webm', '.ogg', '.mov', '.avi']
  const lowerUrl = url.toLowerCase()
  return !videoExtensions.some(ext => lowerUrl.includes(ext)) && 
         (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://') || lowerUrl.startsWith('data:') || lowerUrl.startsWith('/'))
}

function extractImage(item = {}, type) {
  if (!item) return null
  // Verificăm mai întâi câmpurile cu prefix v3_
  let image = null
  if (type === 'desktop') {
    image = item.v3_image_16_9 || item.v3_image_9_16 || item.v3_image || item.image_16_9 || item.image_9_16 || item.image || null
  } else {
    image = item.v3_image_9_16 || item.v3_image_16_9 || item.v3_image || item.image_9_16 || item.image_16_9 || item.image || null
  }
  // Verificăm dacă este o imagine validă (nu un manifest video)
  return isValidImageUrl(image) ? image : null
}

function getSelectedItemsKey(widgetType) {
  switch (widgetType) {
    case 'live-streams':
      return 'selectedLiveStreams'
    case 'people':
      return 'selectedPeople'
    case 'radios':
      return 'selectedRadios'
    case 'sites':
      return 'selectedSites'
    case 'programs':
      return 'selectedPrograms'
    case 'watching':
      return null // Watching nu suportă selecție manuală
    case 'whats-new':
      return 'selectedPrograms'
    default:
      return 'selectedItems'
  }
}

function getUserFirstName() {
  try {
    const stored = JSON.parse(localStorage.getItem('userData') || '{}')
    if (stored && stored.first_name) return stored.first_name
    if (stored && stored.name) {
      return String(stored.name).split(' ')[0]
    }
  } catch (e) {
    // ignore
  }
  return 'invitat'
}

const ArrowButton = ({ direction, onClick }) => {
  const isNext = direction === 'next'
  return (
    <button
      className={`v3-arrow v3-arrow-${direction}`}
      type="button"
      onClick={onClick}
      aria-label={isNext ? 'Vezi următoarele' : 'Vezi precedentele'}
    >
      {isNext ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 6l6 6l-6 6" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 6l-6 6l6 6" />
        </svg>
      )}
    </button>
  )
}

const V3Home = () => {
  const navigate = useNavigate()
  const [design, setDesign] = useState({ widgets: [] })
  const [catalog, setCatalog] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [userRefresh, setUserRefresh] = useState(0)
  // Verificăm dacă splash-ul a fost deja afișat în această sesiune
  const [showVideoSplash, setShowVideoSplash] = useState(() => {
    return !sessionStorage.getItem('v3_splash_shown')
  })
  const [homepageVisible, setHomepageVisible] = useState(() => {
    // Dacă splash-ul a fost deja afișat, homepage-ul poate fi vizibil imediat
    return !!sessionStorage.getItem('v3_splash_shown')
  })
  const [watchingEpisodes, setWatchingEpisodes] = useState([]) // State pentru episoadele watching cu poster-uri
  const firstName = useMemo(() => getUserFirstName(), [userRefresh])
  const isLoggedIn = useMemo(() => !!localStorage.getItem('authToken'), [userRefresh])

  // Încarcă homepage-ul complet (design + catalog) dintr-un singur request
  useEffect(() => {
    let isMounted = true
    async function loadHomepage() {
      setLoading(true)
      try {
        const res = await fetch(`${API_BASE}/homepagev3`)
        if (!res.ok) {
          throw new Error(`Failed to load homepage: ${res.status}`)
        }
        
        // Citim răspunsul ca text mai întâi pentru a verifica validitatea
        const text = await res.text()
        let response
        try {
          response = JSON.parse(text)
        } catch (parseError) {
          console.error('[V3Home] JSON parse error:', parseError, 'Response text:', text.substring(0, 500))
          throw new Error('Răspuns invalid de la server')
        }
        
        if (isMounted && response?.success && response?.data) {
          // Setăm design-ul
          const designData = response.data.design || { widgets: [] }
          setDesign(designData)
          
          // Mapăm datele din catalog la structura așteptată (cu prefix v3_)
          const catalogData = response.data.catalog || {}
          
          // Validăm și sanitizăm datele din catalog
          const nextCatalog = {
            'live-streams': Array.isArray(catalogData.v3_live_streams) ? catalogData.v3_live_streams : [],
            'sites': Array.isArray(catalogData.v3_sites) ? catalogData.v3_sites : [],
            'programs': Array.isArray(catalogData.v3_programs) ? catalogData.v3_programs : [],
            'people': Array.isArray(catalogData.v3_people) ? catalogData.v3_people : [],
            'radios': Array.isArray(catalogData.v3_radios) ? catalogData.v3_radios : [],
            'watching': Array.isArray(catalogData.v3_watching) ? catalogData.v3_watching : [],
            'whats-new': Array.isArray(catalogData.v3_whats_new) ? catalogData.v3_whats_new : [],
            'news': Array.isArray(catalogData.v3_news) ? catalogData.v3_news : []
          }
          
          setCatalog(nextCatalog)
          setError(null)
          
          // Track homepage view
          trackHomepageView()
        } else {
          console.warn('[V3Home] Răspuns invalid sau isMounted = false')
        }
      } catch (err) {
        console.error('[V3Home] Failed to load homepage', err)
        if (isMounted) {
          setError('Nu am putut încărca homepage-ul. Încearcă din nou.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadHomepage()
    return () => {
      isMounted = false
    }
  }, [])

  // Actualizează user info
  useEffect(() => {
    const handleFocus = () => {
      setUserRefresh(prev => prev + 1)
    }

    window.addEventListener('focus', handleFocus)
    setUserRefresh(prev => prev + 1)

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('userData')
    localStorage.removeItem('subscriptionId')
    localStorage.removeItem('subscriptionLevel')
    localStorage.removeItem('__paypal_storage__')
    navigate('/v3.0/login')
    window.location.reload()
  }

  // Funcție pentru obținerea episoadelor începute dar neterminate
  function getWatchingEpisodes(allPrograms, maxElements, daysBack) {
    try {
      const watchData = JSON.parse(localStorage.getItem('watchProgress') || '{}')
      const now = Date.now()
      const daysBackMs = (daysBack || 30) * 24 * 60 * 60 * 1000
      
      const watchingEpisodes = []
      
      // Iterăm prin programele urmărite
      Object.keys(watchData).forEach(programId => {
        const programEpisodes = watchData[programId]
        // Programele din catalog au v3_id, nu id
        const program = allPrograms.find(p => (p.v3_id || p.id) === parseInt(programId))
        
        if (!program) {
          return
        }
        
        // Verificăm dacă există episoade neterminate
        Object.keys(programEpisodes).forEach(episodeId => {
          const episodeData = programEpisodes[episodeId]
          
          // Verificăm dacă episodul nu este completat și a fost vizionat în ultimele X zile
          if (!episodeData.completed && episodeData.lastWatched) {
            const timeSinceWatch = now - episodeData.lastWatched
            if (timeSinceWatch <= daysBackMs) {
              // Folosim poster-urile episodului dacă sunt disponibile, altfel folosim pozele programului
              const episodePoster16_9 = episodeData.poster_image_16_9
              const episodePoster9_16 = episodeData.poster_image_9_16
              
              // Adăugăm episodul cu toate informațiile necesare
              // Programele din catalog au v3_image_16_9 și v3_image_9_16, nu image_16_9 și image_9_16
              const programImage16_9 = program.v3_image_16_9 || program.image_16_9
              const programImage9_16 = program.v3_image_9_16 || program.image_9_16
              
              watchingEpisodes.push({
                id: parseInt(episodeId),
                programId: parseInt(programId),
                title: episodeData.episodeTitle || 'Episod',
                description: '',
                // Folosim poster-urile episodului dacă există, altfel fallback la program
                image_16_9: episodePoster16_9 || programImage16_9,
                image_9_16: episodePoster9_16 || programImage9_16,
                image: episodePoster16_9 || episodePoster9_16 || programImage16_9 || programImage9_16,
                program: program,
                currentTime: episodeData.currentTime || 0,
                duration: episodeData.duration || 0,
                lastWatched: episodeData.lastWatched,
                // Date episod pentru link
                episode: {
                  id: parseInt(episodeId),
                  title: episodeData.episodeTitle || 'Episod',
                  program: program
                }
              })
            }
          }
        })
      })
      
      // Sortăm după data ultimei vizionări (cele mai recente primele)
      watchingEpisodes.sort((a, b) => b.lastWatched - a.lastWatched)
      
      // Limităm la numărul maxim de elemente
      return watchingEpisodes.slice(0, maxElements || 10)
    } catch (error) {
      console.error('Eroare la obținerea episoadelor watching:', error)
      return []
    }
  }
  
  // Funcție pentru încărcarea poster-urilor episoadelor din API dacă nu sunt în localStorage
  async function loadEpisodePosters(watchingEpisodes) {
    // Verificăm dacă episoadele au poster-uri salvate în localStorage
    const episodesToLoad = watchingEpisodes.filter(ep => {
      try {
        const watchData = JSON.parse(localStorage.getItem('watchProgress') || '{}')
        const episodeData = watchData[ep.programId]?.[ep.id]
        // Încărcăm dacă nu avem poster-uri salvate sau dacă imaginile folosite sunt din program
        return !episodeData?.poster_image_16_9 && !episodeData?.poster_image_9_16
      } catch {
        return true
      }
    })
    
    if (episodesToLoad.length === 0) return watchingEpisodes
    
    // Încărcăm poster-urile în paralel
    const loadPromises = episodesToLoad.map(async (episode) => {
      try {
        const response = await fetch(`${API_BASE}/programs/${episode.programId}/episodes/${episode.id}`)
        if (!response.ok) return episode
        
        const json = await response.json()
        if (json.success && json.data) {
          const episodeData = json.data
          // Actualizăm poster-urile în localStorage
          try {
            const watchData = JSON.parse(localStorage.getItem('watchProgress') || '{}')
            if (watchData[episode.programId] && watchData[episode.programId][episode.id]) {
              watchData[episode.programId][episode.id].poster_image_16_9 = episodeData.poster_image_16_9
              watchData[episode.programId][episode.id].poster_image_9_16 = episodeData.poster_image_9_16
              localStorage.setItem('watchProgress', JSON.stringify(watchData))
            }
          } catch (e) {
            console.error('Eroare la actualizarea localStorage:', e)
          }
          
          // Returnăm episodul actualizat cu poster-urile episodului
          return {
            ...episode,
            image_16_9: episodeData.poster_image_16_9 || episode.image_16_9,
            image_9_16: episodeData.poster_image_9_16 || episode.image_9_16,
            image: episodeData.poster_image_16_9 || episodeData.poster_image_9_16 || episode.image
          }
        }
      } catch (error) {
        console.error(`Eroare la încărcarea poster-ului pentru episod ${episode.id}:`, error)
      }
      return episode
    })
    
    const loadedEpisodes = await Promise.all(loadPromises)
    
    // Actualizăm episoadele cu poster-urile încărcate
    return watchingEpisodes.map(ep => {
      const loaded = loadedEpisodes.find(le => le.id === ep.id && le.programId === ep.programId)
      return loaded || ep
    })
  }

  // Renderează un widget pe baza configurației
  const renderWidget = (widget, index) => {
    const config = WIDGET_TYPE_MAP[widget.type]
    if (!config) {
      console.warn(`Unknown widget type: ${widget.type}`)
      return null
    }

    let items = catalog[widget.type] || []
    
    // Pentru widget-ul watching, filtrează episoadele începute dar neterminate
    if (widget.type === 'watching') {
      const maxElements = widget.settings?.maxElements || 10
      const daysBack = widget.settings?.daysBack || 30
      
      const initialEpisodes = getWatchingEpisodes(items, maxElements, daysBack)
      
      // Dacă avem episoade și nu sunt deja încărcate, încărcăm poster-urile
      if (initialEpisodes.length > 0 && watchingEpisodes.length === 0) {
        loadEpisodePosters(initialEpisodes).then(loadedEpisodes => {
          setWatchingEpisodes(loadedEpisodes)
        })
        items = initialEpisodes // Afișăm cu fallback la program images până se încarcă
      } else if (watchingEpisodes.length > 0) {
        // Folosim episoadele cu poster-uri încărcate
        items = watchingEpisodes
      } else {
        items = initialEpisodes
      }
    } else if (widget.type === 'whats-new') {
      // Pentru widget-ul whats-new, filtrează programele apărute în ultimele X zile
      const daysBack = widget.settings?.daysBack || 30
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - daysBack)
      
      // Filtrează programele create în ultimele X zile
      items = items.filter(item => {
        const createdAt = item.v3_created_at || item.created_at
        if (!createdAt) return false
        const createdDate = new Date(createdAt)
        return createdDate >= cutoffDate
      })
      
      // Ordonează după data creării (cele mai noi primele)
      items.sort((a, b) => {
        const dateA = (a.v3_created_at || a.created_at) ? new Date(a.v3_created_at || a.created_at) : new Date(0)
        const dateB = (b.v3_created_at || b.created_at) ? new Date(b.v3_created_at || b.created_at) : new Date(0)
        return dateB - dateA
      })
      
      // Aplică filtrarea pe programe specifice dacă există
      const selectedItemsKey = getSelectedItemsKey(widget.type)
      if (widget.settings?.[selectedItemsKey] && widget.settings[selectedItemsKey].length > 0) {
        // Ordonează elementele conform ordinii specificate în selectedItems
        const orderedItems = []
        widget.settings[selectedItemsKey].forEach(itemId => {
          const item = items.find(item => (item.v3_id || item.id) === itemId)
          if (item) {
            orderedItems.push(item)
          }
        })
        items = orderedItems
      }
    } else if (widget.type === 'news') {
      // Pentru widget-ul news, filtrează articolele apărute în ultimele X ore
      const hoursBack = widget.settings?.hoursBack || 24
      const cutoffDate = new Date()
      cutoffDate.setHours(cutoffDate.getHours() - hoursBack)
      
      // Filtrează articolele invalide și publicate în ultimele X ore
      items = items.filter((item) => {
        // Verifică că item-ul este valid
        if (!item || typeof item !== 'object') {
          return false
        }
        if (!item.v3_id && !item.id) {
          return false
        }
        if (!item.v3_title && !item.title) {
          return false
        }
        
        const articleDate = item.v3_date || item.date
        if (!articleDate || typeof articleDate !== 'string') {
          return false
        }
        
        try {
          const date = new Date(articleDate)
          // Verifică că data este validă
          if (isNaN(date.getTime())) {
            return false
          }
          return date >= cutoffDate
        } catch (e) {
          return false
        }
      })
      
      // Ordonează după data publicării (cele mai noi primele)
      items.sort((a, b) => {
        try {
          const dateA = (a.v3_date || a.date) ? new Date(a.v3_date || a.date) : new Date(0)
          const dateB = (b.v3_date || b.date) ? new Date(b.v3_date || b.date) : new Date(0)
          
          // Verifică că datele sunt valide
          if (isNaN(dateA.getTime())) return 1
          if (isNaN(dateB.getTime())) return -1
          
          return dateB.getTime() - dateA.getTime()
        } catch (e) {
          return 0
        }
      })
    } else {
      // Pentru toate tipurile de widget-uri, filtrează și ordonează după selectedItems dacă există
      const selectedItemsKey = getSelectedItemsKey(widget.type)
      if (widget.settings?.[selectedItemsKey] && widget.settings[selectedItemsKey].length > 0) {
        // Ordonează elementele conform ordinii specificate în selectedItems
        const orderedItems = []
        widget.settings[selectedItemsKey].forEach(itemId => {
          const item = items.find(item => (item.v3_id || item.id) === itemId)
          if (item) {
            orderedItems.push(item)
          }
        })
        items = orderedItems
      }
    }
    
    const limit = widget.settings?.showAll ? items.length : (widget.settings?.limit || widget.settings?.maxElements || 6)
    const displayItems = items.slice(0, limit)
    const title = widget.settings?.title || widget.label || 'Secțiune'

    if (displayItems.length === 0) {
      return null // Nu afișa widget-uri goale
    }

    return (
      <section key={`widget-${widget.type}-${index}`} className="v3-section">
        <header className="v3-section-header">
          <h2>{title}</h2>
        </header>
        <div className="v3-row">
          <Slider
            slidesToShow={6}
            slidesToScroll={6}
            infinite={false}
            arrows
            dots
            nextArrow={<ArrowButton direction="next" />}
            prevArrow={<ArrowButton direction="prev" />}
            responsive={[
              { breakpoint: 1280, settings: { slidesToShow: 5, slidesToScroll: 5 } },
              { breakpoint: 1024, settings: { slidesToShow: 4, slidesToScroll: 4 } },
              { breakpoint: 768, settings: { slidesToShow: 4, slidesToScroll: 4, arrows: false, dots: true } },
              { breakpoint: 640, settings: { slidesToShow: 4, slidesToScroll: 4, arrows: false, dots: true } }
            ]}
          >
            {displayItems.map((item, itemIndex) => {
              // Pentru news, folosim imaginea direct din articol (dar verificăm că nu e manifest video)
              const itemId = item.v3_id || item.id
              
              // Pentru news, verificăm mai întâi dacă există imagini valide (nu manifeste video)
              let newsImage = null
              let newsPreviewVideo = null // MP4 preview de la JWPlayer
              
              if (widget.type === 'news') {
                // Verificăm dacă există un manifest JWPlayer în câmpul nou v3_video_manifest
                let manifestUrl = item.v3_video_manifest || null
                
                if (!manifestUrl) {
                  // Dacă nu există câmpul v3_video_manifest, verificăm în v3_image
                  const candidates = [
                    item.v3_image_16_9,
                    item.v3_image_9_16,
                    item.v3_image,
                    item.image_16_9,
                    item.image_9_16,
                    item.image
                  ]
                  
                  for (const candidate of candidates) {
                    if (candidate && typeof candidate === 'string') {
                      // Verificăm dacă este manifest (.m3u8) sau video MP4 de la JWPlayer
                      if (candidate.includes('jwplayer.com/manifests/') || 
                          candidate.includes('jwplatform.com/manifests/') ||
                          candidate.includes('jwplayer.com/videos/') ||
                          candidate.includes('jwplatform.com/videos/')) {
                        manifestUrl = candidate
                        break
                      }
                    }
                  }
                }
                
                if (manifestUrl) {
                  // Dacă există un manifest, încercăm să folosim preview MP4
                  const mediaId = extractJWPlayerMediaId(manifestUrl)
                  if (mediaId) {
                    // Folosim direct preview-ul (fără verificare - dacă nu există, video element va folosi onError)
                    newsPreviewVideo = getJWPlayerPreviewUrl(mediaId)
                    newsImage = getJWPlayerPosterUrl(mediaId) // Poster-ul ca fallback
                  }
                } else {
                  // Dacă nu există manifest, căutăm imagini normale
                  const candidates = [
                    item.v3_image_16_9,
                    item.v3_image_9_16,
                    item.v3_image,
                    item.image_16_9,
                    item.image_9_16,
                    item.image
                  ]
                  
                  for (const candidate of candidates) {
                    if (isValidImageUrl(candidate)) {
                      newsImage = candidate
                      break
                    }
                  }
                }
              }
              
              const desktopImage = widget.type === 'news' 
                ? (newsImage || appIcon)
                : (extractImage(item, 'desktop') || appIcon)
              const mobileImage = widget.type === 'news'
                ? (newsImage || appIcon)
                : (extractImage(item, 'mobile') || desktopImage)
              const itemTitle = extractName(item, config.nameField)
              
              // Pentru watching, construim link către episod
              let linkTo = null
              let linkState = {}
              
              if (widget.type === 'watching' && item.programId && itemId) {
                linkTo = `/v3.0/programs/${item.programId}/episodes/${itemId}`
                linkState = {
                  program: item.program,
                  episode: item.episode || {
                    id: itemId,
                    title: item.v3_title || item.title,
                    program: item.program
                  },
                  resumeFrom: item.currentTime || 0 // Trimitem currentTime pentru a continua de unde a rămas
                }
              } else if (widget.type === 'news' && (item.v3_link || item.link)) {
                // Pentru news, folosim link-ul direct din articol (extern)
                linkTo = item.v3_link || item.link
                linkState = {}
              } else {
                linkTo = itemId ? `${config.linkBase}/${itemId}` : null
                linkState = { [widget.type === 'radio' ? 'radio' : widget.type.replace('-', '_').replace(/s$/, '')]: item }
              }
              
              const cardContent = (
                <>
                  <div className="v3-card-image">
                    {widget.type === 'news' && newsPreviewVideo ? (
                      // Dacă avem preview MP4, folosim video element
                      <>
                        <video
                          src={newsPreviewVideo}
                          poster={desktopImage}
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="v3-card-video-preview"
                          onError={(e) => {
                            // Dacă video-ul nu se încarcă, ascundem video-ul și afișăm imaginea
                            console.warn('[V3Home] [NEWS-VIDEO-ERROR] Preview video nu s-a încărcat:', newsPreviewVideo, 'folosim poster')
                            e.target.style.display = 'none'
                            const fallbackImg = e.target.parentElement.querySelector('.v3-card-video-fallback')
                            if (fallbackImg) {
                              fallbackImg.style.display = 'block'
                            }
                          }}
                        />
                        {/* Fallback la imagine dacă video nu se încarcă */}
                        <img 
                          src={desktopImage} 
                          alt={itemTitle} 
                          className="v3-card-video-fallback"
                          style={{ display: 'none' }}
                          loading="lazy"
                          onError={(e) => {
                            if (e.target.src !== appIcon) {
                              console.warn('[V3Home] [NEWS-IMG-ERROR] Imaginea nu s-a încărcat:', e.target.src, 'folosim fallback')
                              e.target.src = appIcon
                            }
                          }}
                        />
                      </>
                    ) : (
                      // Altfel folosim imaginea normală
                      <picture>
                        <source media="(max-width: 768px)" srcSet={mobileImage} />
                        <img 
                          src={desktopImage} 
                          alt={itemTitle} 
                          loading="lazy"
                          onError={(e) => {
                            // Dacă imaginea nu se încarcă, folosim fallback
                            if (e.target.src !== appIcon) {
                              console.warn('[V3Home] [NEWS-IMG-ERROR] Imaginea nu s-a încărcat:', e.target.src, 'folosim fallback')
                              e.target.src = appIcon
                            }
                          }}
                        />
                      </picture>
                    )}
                  </div>
                  {widget.settings?.showCardText === true && (
                    <div className="v3-card-title">{itemTitle}</div>
                  )}
                </>
              )

              // Pentru news, folosim link extern
              if (widget.type === 'news' && linkTo) {
                return (
                  <a 
                    href={linkTo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="v3-card v3-card--link" 
                    key={`${widget.type}-${item?.id ?? 'placeholder'}-${itemIndex}`}
                  >
                    {cardContent}
                  </a>
                )
              }

              if (linkTo) {
                return (
                  <Link 
                    to={linkTo} 
                    state={linkState}
                    className="v3-card v3-card--link" 
                    key={`${widget.type}-${item?.id ?? 'placeholder'}-${itemIndex}`}
                  >
                    {cardContent}
                  </Link>
                )
              }

              return (
                <div className="v3-card" key={`${widget.type}-${item?.id ?? 'placeholder'}-${itemIndex}`}>
                  {cardContent}
                </div>
              )
            })}
          </Slider>
        </div>
      </section>
    )
  }

  const handleVideoComplete = () => {
    // Marchează că splash-ul a fost afișat în această sesiune
    sessionStorage.setItem('v3_splash_shown', 'true')
    // Așteaptă să se afișeze imaginea (800ms) apoi trece la homepage
    setTimeout(() => {
      setShowVideoSplash(false)
      // După ce splash-ul dispare, trigger fade-in pentru homepage
      setTimeout(() => {
        setHomepageVisible(true)
      }, 100)
    }, 800)
  }

  return (
    <>
      {showVideoSplash && (
        <V3VideoSplash onComplete={handleVideoComplete} />
      )}
      <div className="v3-root" style={{ backgroundImage: `url(${lastFrameImg})` }}>
        <div className={`v3-overlay ${homepageVisible && !showVideoSplash ? 'fade-in' : ''}`}>
        <header className="v3-header">
          <div className="v3-header-left">
            Pentru tine, <span>{firstName}</span>
          </div>
          <div className="v3-header-center">
            
          </div>
          <div className="v3-header-right">
            <img src={alephLogo} alt="Aleph ix" className="v3-logo-wordmark" />
            {isLoggedIn && (
              <>
                <button 
                  className="v3-profile-btn" 
                  onClick={() => navigate('/v3.0/account')}
                  aria-label="Profilul meu"
                  title="Profilul meu"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </button>
                <button 
                  className="v3-logout-btn" 
                  onClick={handleLogout}
                  aria-label="Deconectare"
                  title="Deconectare"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </header>

        {loading && (
          <div className="v3-status">Se încarcă homepage-ul...</div>
        )}
        {error && <div className="v3-status v3-status-error">{error}</div>}

        {!loading && !error && (
          <div className="v3-sections">
            {design.widgets && design.widgets.length > 0 ? (
              design.widgets.map((widget, index) => renderWidget(widget, index))
            ) : (
              <div className="v3-empty-state">
                <p>Nu există widget-uri configurate încă.</p>
                <p className="v3-empty-hint">Folosește Homepage Builder din panoul de administrare pentru a configura această pagină.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  )
}

export default V3Home

